import styled, { keyframes } from 'styled-components';

/* ── Layout ──────────────────────────────────────────── */

export const MainBody = styled.div`
  background-color: ${({ theme }) => theme.colors.bg_primary};
  min-height: 100vh;
  overflow-x: hidden;
  transition: background-color 0.3s ease;
`;

export const Container = styled.div`
  width: 90%;
  max-width: 1200px;
  margin: 0 auto;
`;

export const Section = styled.section`
  padding: 6rem 0;
  position: relative;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 4rem 0;
  }
`;

export const PaddingContainer = styled.div`
  padding-top: ${({ top }) => top};
  padding-bottom: ${({ bottom }) => bottom};
  padding-left: ${({ left }) => left};
  padding-right: ${({ right }) => right};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding-top: ${({ responsiveTop }) => responsiveTop};
    padding-bottom: ${({ responsiveBottom }) => responsiveBottom};
    padding-left: ${({ responsiveLeft }) => responsiveLeft};
    padding-right: ${({ responsiveRight }) => responsiveRight};
  }
`;

export const FlexContainer = styled.div`
  display: flex;
  justify-content: ${({ justify }) => justify};
  align-items: ${({ align }) => align};
  gap: ${({ gap }) => gap};
  flex-direction: ${({ direction }) => direction};
  flex-wrap: ${({ wrap }) => wrap};

  & > div {
    flex: ${({ fullWidthChild }) => fullWidthChild && 1};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: ${({ responsiveFlex }) => (responsiveFlex ? 'flex' : 'block')};
    flex-direction: ${({ responsiveDirection }) => responsiveDirection};
  }
`;

/* ── Typography ──────────────────────────────────────── */

export const SectionTitle = styled.p`
  font-size: 0.875rem;
  font-weight: ${({ theme }) => theme.fonts.weight.semibold};
  text-transform: uppercase;
  letter-spacing: 3px;
  color: ${({ theme }) => theme.colors.accent};
  margin-bottom: 0.75rem;
  text-align: ${({ align }) => align || 'left'};
`;

export const Heading = styled(PaddingContainer)`
  color: ${({ theme }) => theme.colors.heading};
  text-align: ${({ align }) => align};
  font-weight: ${({ theme }) => theme.fonts.weight.bold};
  line-height: 1.2;
  letter-spacing: -0.02em;

  font-size: ${({ size }) => {
    switch (size) {
      case 'h1':
        return '4rem';
      case 'h2':
        return '2.5rem';
      case 'h3':
        return '1.75rem';
      case 'h4':
        return '1.125rem';
      default:
        return '1rem';
    }
  }};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ size }) => {
      switch (size) {
        case 'h1':
          return '2.5rem';
        case 'h2':
          return '2rem';
        case 'h3':
          return '1.5rem';
        case 'h4':
          return '1rem';
        default:
          return '1rem';
      }
    }};
  }
`;

export const BlueText = styled.span`
  background: ${({ theme }) => theme.colors.accent_gradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

export const ParaText = styled(PaddingContainer)`
  color: ${({ theme }) => theme.colors.para};
  line-height: 1.8;
  font-size: 1rem;
  max-width: ${({ maxWidth }) => maxWidth || 'none'};
`;

/* ── Interactive ─────────────────────────────────────── */

export const IconContainer = styled.div`
  font-size: ${({ size }) => size || '1.5rem'};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ color, theme }) => {
    switch (color) {
      case 'white':
        return theme.colors.white;
      case 'blue':
        return theme.colors.accent;
      case 'para':
        return theme.colors.para;
      default:
        return theme.colors.accent;
    }
  }};
  transition: ${({ theme }) => theme.transitions.default};

  &:hover {
    color: ${({ theme }) => theme.colors.accent_light};
    transform: translateY(-2px);
  }
`;

export const Button = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: max-content;
  padding: 0.875rem 2rem;
  color: ${({ theme }) => theme.colors.white};
  background: ${({ theme }) => theme.colors.accent_gradient};
  border: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  cursor: pointer;
  font-weight: ${({ theme }) => theme.fonts.weight.semibold};
  font-size: 0.95rem;
  letter-spacing: 0.02em;
  transition: ${({ theme }) => theme.transitions.default};
  box-shadow: ${({ theme }) => theme.shadows.button};
  text-decoration: none;

  &:hover {
    background: ${({ theme }) => theme.colors.accent_gradient_hover};
    transform: translateY(-2px);
    box-shadow: 0 6px 25px rgba(59, 130, 246, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const OutlineButton = styled(Button)`
  background: transparent;
  border: 2px solid ${({ theme }) => theme.colors.accent};
  box-shadow: none;
  color: ${({ theme }) => theme.colors.accent};

  &:hover {
    background: ${({ theme }) => theme.colors.accent_gradient};
    border-color: transparent;
    color: ${({ theme }) => theme.colors.white};
    box-shadow: ${({ theme }) => theme.shadows.button};
  }
`;

/* ── Decorative ──────────────────────────────────────── */

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-20px); }
`;

export const GlowOrb = styled.div`
  position: absolute;
  width: ${({ size }) => size || '300px'};
  height: ${({ size }) => size || '300px'};
  border-radius: 50%;
  background: ${({ color }) =>
    color === 'purple'
      ? 'radial-gradient(circle, rgba(139, 92, 246, 0.15), transparent 70%)'
      : 'radial-gradient(circle, rgba(59, 130, 246, 0.15), transparent 70%)'};
  top: ${({ top }) => top};
  left: ${({ left }) => left};
  right: ${({ right }) => right};
  bottom: ${({ bottom }) => bottom};
  pointer-events: none;
  animation: ${float} 8s ease-in-out infinite;
  z-index: 0;
`;

export const Divider = styled.div`
  width: 60px;
  height: 4px;
  background: ${({ theme }) => theme.colors.accent_gradient};
  border-radius: 2px;
  margin: ${({ center }) => (center ? '1rem auto' : '1rem 0')};
`;
